import { withNamespaces } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import IndustriesService from "src/modules/3rd-party-management/apis/common/IndustriesService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AvField, AvForm, AvInput } from "availity-reactstrap-validation";
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Row,
    Col,
    ModalFooter,
    Label,
    Alert
} from "reactstrap";
import {
    CompanySizes
} from "src/modules/3rd-party-management/constants";
import Spinner from 'react-bootstrap/Spinner';
import SupplierService from "src/modules/3rd-party-management/apis/common/SupplierService";
import { CarrierCategories } from "../../../../../constants";
import { MOCK_PARENT_THIRD_PARTY_OPTIONS } from "../../../../../mock/subcontractors";
import { setStoredParentThirdPartyId } from "../../../../../mock/parentThirdPartyStorage";
import DatePicker from "react-datepicker";
import * as datePickerLocales from "date-fns/locale";
import { INTERNATIONAL_DATE_FORMAT } from "src/components/constants";
import ThirdpartyUsersService from "src/modules/3rd-party-management/apis/common/ThirdpartyUsersService";
import DateUtils from "src/services/utils/DateUtils";
import useVatNumberCheck from "src/modules/3rd-party-management/hooks/useVatNumberCheck";
import VatNumberCheckFeedback from "src/modules/3rd-party-management/components/VatNumberCheckFeedback";

const industriesFilterOption = (
    candidate,
    input
) => {
    if (input) {
        const candidateInput = candidate.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");
        const normalizedInput = input.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");

        return candidateInput.indexOf(normalizedInput) >= 0 || ((candidate.data.keywords || []).findIndex((keyword) => {
            const normalizedKeyword = keyword.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");

            return normalizedKeyword.indexOf(normalizedInput) >= 0
        }) >= 0);
    }

    return true;
};

const EditSupplierGeneralInfosModal = ({
    t,
    lng,
    supplierId,
    closeModal,
    onComplete,
    parentThirdParty,
    onParentThirdPartyChange,
}) => {
    const dateUtils = new DateUtils();

    const editGeneralInfosFormRef = useRef();

    const [showIndustryDeletionError, setShowIndustryDeletionError] = useState(null);
    const [selectedIndustries, setSelectedIndustries] = useState([]);
    const [industriesQuery, setIndustriesQuery] = useState({
        isLoading: false,
        itemsCount: 0,
        pageIndex: 1,
        pageSize: 1000,
        searchText: '',
        items: []
    });
    const [ birthDate, setBirthDate ] = useState(null);
    const [ birthCountry, setBirthCountry ] = useState(null);
    const [ selectedParentThirdParty, setSelectedParentThirdParty ] = useState(null);

    const vatNumberCheck = useVatNumberCheck();

    const {
        data: selectedSupplierToEditInfos,
        refetch: fetchSupplierInfos,
        isFetching: supplierInfosIsFetching
    } = useQuery({
        queryKey: ['3rd-party-management-supplier-details-basic-infos-modal', supplierId],
        queryFn: async () => {
            const service = SupplierService.getInstance();

            return await service.fetchBasicInfos(supplierId, {});
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: false,
        onError: () => {
            toast(t('An error occurred while fetching the basic information.'), {
                type: 'error',
            });
        }
    });

    const {
        refetch: refetchIndustries
    } = useQuery({
        queryKey: [
            '3rd-party-management-search-industries-query',
            lng
        ],
        queryFn: async () => {
            const service = IndustriesService.getInstance();

            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: true
                }
            });

            return await service.fetchList({
                language: lng !== 'en' ? lng : undefined,
                pageSize: industriesQuery.pageSize,
                pageIndex: industriesQuery.pageIndex,
                name: industriesQuery.searchText || undefined
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: selectedSupplierToEditInfos?.type && selectedSupplierToEditInfos.type !== 'individual',
        onError: () => {
            toast(t('An error occurred while fetching industries list.'), {
                type: 'error',
            });
        },
        onSuccess: (response) => {
            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: false,
                    // itemsCount: parseInt(response.itemsCount),
                    // pageIndex: parseInt(response.pageIndex),
                    // pageSize: parseInt(response.pageSize),
                    items: (response || []).map((industry) => {
                        return {
                            value: industry.id,
                            label: `${industry.class} - ${industry.title}`,
                            keywords: industry?.keywords || []
                        }
                    })
                }
            });
        },
        onSettled: () => {
            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: false
                }
            });
        }
    });

    const countriesQuery = useQuery({
        queryKey: ['3rd-party-management-fetch-countries-list'],
        queryFn: async () => {
            const service = ThirdpartyUsersService.getInstance();
            return await service.fetchCountries();
        },
        cacheTime: 0,
        enabled: selectedSupplierToEditInfos?.type === 'individual',
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching countries list.'), {
                type: 'error'
            });
        }
    });

    const {
        mutate: updateSupplierGeneralInfosMutation,
        isLoading: updateSupplierGeneralInfosMutationInProcess
    } = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierService.getInstance();
            return await service.updateGeneralInfos(payload);
        },
        onSuccess: () => {
            toast(t("General information updated successfully."), {
                type: "success",
            });
            onComplete && onComplete();
            closeModal();
        },
        onError: () => {
            toast(t("An error occurred while updating General information."), {
                type: "error",
            });
        }
    });

    const handleValidateIndustryDeletion = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierService.getInstance();
            return await service.validateIndustryDeletion(payload);
        }
    });

    useEffect(() => {
        if (supplierId) {
            fetchSupplierInfos();
        }
    }, [supplierId]);

    useEffect(() => {
        if (supplierId) {
            setSelectedParentThirdParty(parentThirdParty || null);
        }
    }, [supplierId, parentThirdParty]);

    useEffect(() => {
        if (selectedSupplierToEditInfos?.industries) {
            const result = (selectedSupplierToEditInfos.industries || []).map((selectedIndustry) => {
                const translatedIndustry = (industriesQuery.items || []).find((industry) => industry.value === selectedIndustry.id);

                return {
                    value: selectedIndustry.id,
                    label: translatedIndustry?.label || selectedIndustry.name,
                    baseLabel: translatedIndustry?.baseLabel,
                    keywords: translatedIndustry?.keywords
                }
            });

            setSelectedIndustries(result);
        }

        if (selectedSupplierToEditInfos?.individualDateOfBirth) {
            setBirthDate(new Date(selectedSupplierToEditInfos.individualDateOfBirth))
        }

        if (selectedSupplierToEditInfos?.individualBirthPlaceCountry?.id) {
            setBirthCountry({
                value: selectedSupplierToEditInfos.individualBirthPlaceCountry.id,
                baseLabel: selectedSupplierToEditInfos.individualBirthPlaceCountry.name,
                label: t(selectedSupplierToEditInfos.individualBirthPlaceCountry.name),
                code: selectedSupplierToEditInfos.individualBirthPlaceCountry.code,
            })
        }
    }, [selectedSupplierToEditInfos]);

    useEffect(() => {
        if (selectedIndustries.length) {
            const result = selectedIndustries.map((selectedIndustry) => {
                const translatedIndustry = (industriesQuery.items || []).find((industry) => industry.value === selectedIndustry.value);

                return translatedIndustry || selectedIndustry;
            });

            setSelectedIndustries(result);
        }
    }, [
        t,
        industriesQuery.items
    ]);

    useEffect(() => {
        const timeOutId = setTimeout(() => {
            refetchIndustries();
        }, 500);

        return () => clearTimeout(timeOutId);
    }, [industriesQuery.searchText]);

    const memoizedCountriesList = useMemo(() => {
        return (countriesQuery.data || []).map((country) => {
            return {
                value: country.id,
                baseLabel: country.name,
                label: t(country.name),
                code: country.code,
            };
        })
    }, [ countriesQuery.data, t ]);

    const MultiValueRemove = (props) => {
        const isLoading = props.data.isLoading;
        return (
            <components.MultiValueRemove {...props}>
                {isLoading && <Spinner color="secondary" size="sm" />}
            </components.MultiValueRemove>
        );
    };

    const handleChangeIndsutries = (selectedOptions, e) => {
        const industryInput = editGeneralInfosFormRef.current._inputs['industries'];
        const selectedValues = selectedOptions.map(option => String(option.value));
        industryInput.value = selectedValues; // Assigning the array directly
        industryInput.validate();

        if (e.action === 'remove-value') {
            if (!e.removedValue.isNew) {
                setSelectedIndustries((currentState) => {
                    return [
                        ...currentState.map((industry) => {
                            return {
                                ...industry,
                                isLoading: industry.value === e.removedValue.value ? true : !!(industry.isLoading)
                            }
                        })
                    ]
                });

                handleValidateIndustryDeletion.mutate({
                    supplier: supplierId,
                    industry: e.removedValue.value
                }, {
                    onSettled: () => {
                        setSelectedIndustries((currentState) => {
                            return [
                                ...currentState.map((industry) => {
                                    return {
                                        ...industry,
                                        isLoading: false
                                    }
                                })
                            ]
                        });
                    },
                    onError: ({ response: { data } }) => {
                        setShowIndustryDeletionError(data.error)
                    },
                    onSuccess: () => {
                        setSelectedIndustries((currentState) => {
                            return [
                                ...currentState.filter((industry) => industry.value !== e.removedValue.value)
                            ]
                        });
                    }
                });
            }
            else {
                setSelectedIndustries((currentState) => {
                    return [
                        ...currentState.filter((industry) => industry.value !== e.removedValue.value)
                    ]
                });
            }
        }
        else {
            setSelectedIndustries((currentState) => {
                return [
                    ...selectedOptions.map((industry) => {
                        const isNew = e.option.value === industry.value ? true : !!(industry.isNew)
                        return {
                            ...((currentState || []).find((i) => i.value === industry.value)),
                            ...industry,
                            isNew: isNew
                        }
                    })
                ]
            });
        }
    }

    const sizes = [];

    for (const sizeKey in CompanySizes) {
        sizes.push({
            value: sizeKey,
            label: `${CompanySizes[sizeKey]?.title} ${t("Employees")}`
        });
    }

    const renderParentThirdPartyField = () => {
        if (selectedSupplierToEditInfos?.isOwnCompany) {
            return null;
        }

        return (
            <Col md="6" sm="12" lg="4" className="mb-3">
                <Label for="edit-general-infos-parent-tp">
                    {t('Parent')}:
                </Label>
                <Select
                    isClearable
                    placeholder={`${t('Select')}...`}
                    classNamePrefix="select2-selection"
                    id="edit-general-infos-parent-tp"
                    options={MOCK_PARENT_THIRD_PARTY_OPTIONS}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    value={selectedParentThirdParty}
                    onChange={(option) => setSelectedParentThirdParty(option || null)}
                />
            </Col>
        );
    };

    const renderIndividualBody = () => {
        return (
            <Row>
                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="carrier_category">
                        {t('Category')}: <span className="text-danger">*</span>
                    </Label>
                    <AvField
                        id="carrier_category"
                        name="subType"
                        type="select"
                        errorMessage={t("This field cannot be blank")}
                        validate={{
                            required: { value: true }
                        }}
                        defaultValue={selectedSupplierToEditInfos?.subType}
                    >
                        <option>
                            {t('Select...')}
                        </option>
                        {CarrierCategories.map((category) => {
                            return (
                                <option
                                    key={category.value}
                                    value={category.value}
                                >
                                    {t(category.label)}
                                </option>
                            )
                        })}
                    </AvField>
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-name-input">
                        {t("Name")}: <span className="text-danger">*</span>
                    </Label>
                    <AvField
                        name="name"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: true },
                        }}
                        id="edit-general-infos-name-input"
                        defaultValue={selectedSupplierToEditInfos?.name}
                    />
                </Col>

                {renderParentThirdPartyField()}

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-short-name-input">
                        {t("Nickname / AKA")}:
                    </Label>
                    <AvField
                        name="shortName"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: {
                                value: false
                            }
                        }}
                        id="edit-general-infos-short-name-input"
                        defaultValue={selectedSupplierToEditInfos?.shortName}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-localLanguageFullName">
                        {t("Name in Local Language")}:
                    </Label>
                    <AvField
                        name="localLanguageFullName"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        id="edit-general-infos-localLanguageFullName"
                        defaultValue={selectedSupplierToEditInfos?.localLanguageFullName}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-localLanguageShortName">
                        {t("Nickname / AKA in Local Language")}:
                    </Label>
                    <AvField
                        name="localLanguageShortName"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        id="edit-general-infos-localLanguageShortName"
                        defaultValue={selectedSupplierToEditInfos?.localLanguageShortName}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-dateOfBirth-input">
                        {t("Date of Birth")}:
                    </Label>
                    <DatePicker
                        locale={datePickerLocales[lng]}
                        name="dateOfBirth"
                        id="edit-general-infos-dateOfBirth-input"
                        className="form-control"
                        selected={birthDate}
                        showTimeSelect={false}
                        dateFormat={INTERNATIONAL_DATE_FORMAT}
                        onChange={(date) => {
                            setBirthDate(date)
                        }}
                    />

                    <AvInput
                        id="edit-general-infos-dateOfBirth-input2"
                        name="individualDateOfBirth"
                        placeholder=""
                        type="hidden"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        value={birthDate ? dateUtils.convertDateToDate(
                            birthDate, 'YYYY-MM-DD'
                        ) : ''}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-countryOfBirth-input">
                        {t("Country of Birth")}:
                    </Label>
                    <Select
                        id="edit-general-infos-countryOfBirth-input"
                        placeholder={t("Select...")}
                        classNamePrefix="select2-selection"
                        options={memoizedCountriesList}
                        menuPortalTarget={document.body}
                        onChange={(e) => {
                            setBirthCountry(e)
                        }}
                        value={birthCountry}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                    <AvInput
                        id="edit-general-infos-countryOfBirth-input2"
                        name="individualBirthPlaceCountry"
                        placeholder=""
                        type="hidden"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        value={birthCountry?.value}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-taxId-input">
                        {t("Tax ID")}:
                    </Label>
                    <AvField
                        name="individualTaxId"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        id="edit-general-infos-taxId-input"
                        defaultValue={selectedSupplierToEditInfos?.individualTaxId}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-nationalId-number-input">
                        {t("National ID Number")}:
                    </Label>
                    <AvField
                        name="individualNationalIdNumber"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        id="edit-general-infos-nationalId-number-input"
                        defaultValue={selectedSupplierToEditInfos?.individualNationalIdNumber}
                    />
                </Col>
            </Row>
        )
    }

    const renderCompanyBody = () => {
        return (
            <Row>
                {!selectedSupplierToEditInfos?.isOwnCompany && (
                    <>
                        <Col md="6" sm="12" lg="4" className="mb-3">
                            <Label for="carrier_category">
                                {t('Category')}: <span className="text-danger">*</span>
                            </Label>
                            <AvField
                                id="carrier_category"
                                name="subType"
                                type="select"
                                errorMessage={t("This field cannot be blank")}
                                validate={{
                                    required: { value: true }
                                }}
                                defaultValue={selectedSupplierToEditInfos?.subType}
                            >
                                <option>
                                    {t('Select...')}
                                </option>
                                {CarrierCategories.map((category) => {
                                    return (
                                        <option
                                            key={category.value}
                                            value={category.value}
                                        >
                                            {t(category.label)}
                                        </option>
                                    )
                                })}
                            </AvField>
                        </Col>
                    </>
                )}

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-name-input">
                        {t("Name")}: <span className="text-danger">*</span>
                    </Label>
                    <AvField
                        name="name"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: true },
                        }}
                        id="edit-general-infos-name-input"
                        defaultValue={selectedSupplierToEditInfos?.name}
                    />
                </Col>

                {renderParentThirdPartyField()}

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-short-name-input">
                        {t("Short Name")}: {selectedSupplierToEditInfos?.isOwnCompany ? <span className="text-danger">*</span> : null}
                    </Label>
                    <AvField
                        name="shortName"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: {
                                value: !!selectedSupplierToEditInfos?.isOwnCompany
                            }
                        }}
                        id="edit-general-infos-short-name-input"
                        defaultValue={selectedSupplierToEditInfos?.shortName}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-localLanguageFullName">
                        {t("Name in Local Language")}:
                    </Label>
                    <AvField
                        name="localLanguageFullName"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false },
                        }}
                        id="edit-general-infos-localLanguageFullName"
                        defaultValue={selectedSupplierToEditInfos?.localLanguageFullName}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-localLanguageShortName">
                        {t("Short Name in Local Language")}:
                    </Label>
                    <AvField
                        name="localLanguageShortName"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false },
                        }}
                        id="edit-general-infos-localLanguageShortName"
                        defaultValue={selectedSupplierToEditInfos?.localLanguageShortName}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-brands-input">
                        {t("Brands")}:
                    </Label>
                    <AvField
                        name="brands"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        id="edit-general-infos-brands-input"
                        defaultValue={selectedSupplierToEditInfos?.brands}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-vatnumber-input">
                        {t("VAT ID")}:
                    </Label>
                    <AvField
                        name="vatNumber"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false }
                        }}
                        id="edit-general-infos-vatnumber-input"
                        defaultValue={selectedSupplierToEditInfos?.vatNumber}
                        onChange={() => vatNumberCheck.reset()}
                        onBlur={(e) => vatNumberCheck.checkVatNumber(e.target.value)}
                    />

                    <VatNumberCheckFeedback
                        status={vatNumberCheck.status}
                        details={vatNumberCheck.details}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-company-size-input">
                        {t("Company Size")}: <span className="text-danger">*</span>
                    </Label>
                    <Select
                        placeholder={t("Select") + "..."}
                        classNamePrefix="select2-selection"
                        id='edit-general-infos-company-size-input'
                        options={sizes}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        onChange={(e) => {
                            const sizeInput = editGeneralInfosFormRef.current._inputs['companySize'];
                            if (sizeInput) {
                                sizeInput.value = e.value;
                                sizeInput.validate();
                            }
                        }}
                        defaultValue={selectedSupplierToEditInfos?.employeeSize && {
                            value: selectedSupplierToEditInfos.employeeSize,
                            label: CompanySizes[selectedSupplierToEditInfos.employeeSize]?.title
                        }}
                    />
                    <AvField
                        name="companySize"
                        type="hidden"
                        errorMessage={t("This field cannot be blank")}
                        validate={{
                            required: { value: true },
                        }}
                        defaultValue={selectedSupplierToEditInfos?.employeeSize}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-registration-number-input">
                        {t("Registration Number")}:
                    </Label>
                    <AvField
                        name="registrationNumber"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false },
                        }}
                        id="edit-general-infos-registration-number-input"
                        defaultValue={selectedSupplierToEditInfos?.registrationNumber}
                    />
                </Col>

                <Col md="6" sm="12" lg="4" className="mb-3">
                    <Label for="edit-general-infos-duns-number-input">
                        {t("DUNS Number")}:
                    </Label>
                    <AvField
                        name="dunsNumber"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false },
                        }}
                        id="edit-general-infos-duns-number-input"
                        defaultValue={selectedSupplierToEditInfos?.dunsNumber}
                    />
                </Col>

                <Col md="12" sm="12" lg={"4"} className="mb-3">
                    <Label for="edit-general-infos-website-input">
                        {t("Website")}:
                    </Label>
                    <AvField
                        name="websiteUrl"
                        type="text"
                        errorMessage={t("This field cannot be blank")}
                        className="form-control"
                        validate={{
                            required: { value: false },
                            pattern: {
                                value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                errorMessage: t("Please enter a valid URL"),
                            },
                        }}
                        id="edit-general-infos-website-input"
                        defaultValue={selectedSupplierToEditInfos?.websiteUrl}
                    />
                </Col>

                <Col sm="12" md="12" lg="8" className="mb-3">
                    <Label for="edit-general-infos-industry-input">
                        {t("Industries")}: <span className="text-danger">*</span>
                    </Label>
                    <Select
                        isClearable={false}
                        isMulti
                        isLoading={industriesQuery.isLoading}
                        placeholder={t("Select") + "..."}
                        classNamePrefix="select2-selection"
                        id='edit-general-infos-industry-input'
                        options={industriesQuery.items}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        onChange={handleChangeIndsutries}
                        components={{ MultiValueRemove }}
                        filterOption={(e) => e}
                        value={selectedIndustries}
                        onInputChange={(input) => {
                            setIndustriesQuery((currentState) => {
                                return {
                                    ...currentState,
                                    searchText: input
                                }
                            })
                        }}
                        backspaceRemovesValue={false}
                    />
                    <AvField
                        name="industries"
                        type="hidden"
                        errorMessage={t("This field cannot be blank")}
                        validate={{
                            required: { value: true },
                        }}
                        value={(selectedIndustries || []).map((industry) => industry.value.toString())}
                    />
                </Col>
            </Row>
        )
    }

    const renderModalBody = () => {
        if (supplierInfosIsFetching) {
            return (
                <Row>
                    <Col sm="12" md="6">
                        <p style={{ width: '50%', height: '8px' }} className={`dt-field dt-skeleton mb-1`}></p>
                        <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col sm="12" md="6">
                        <p style={{ width: '50%', height: '8px' }} className={`dt-field dt-skeleton mb-1`}></p>
                        <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col sm="12" md="6">
                        <p style={{ width: '50%', height: '8px' }} className={`dt-field dt-skeleton mb-1`}></p>
                        <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    </Col>

                    <Col sm="12" md="6">
                        <p style={{ width: '50%', height: '8px' }} className={`dt-field dt-skeleton mb-1`}></p>
                        <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col sm="12" md="6">
                        <p style={{ width: '50%', height: '8px' }} className={`dt-field dt-skeleton mb-1`}></p>
                        <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    </Col>
                    <Col sm="12" md="6">
                        <p style={{ width: '50%', height: '8px' }} className={`dt-field dt-skeleton mb-1`}></p>
                        <p style={{ width: '100%', height: '34px' }} className={`dt-field dt-skeleton`}></p>
                    </Col>
                </Row>
            )
        }

        if (selectedSupplierToEditInfos?.type === 'individual') {
            return renderIndividualBody()
        }

        return renderCompanyBody()
    }

    return (
        <>
            <Modal isOpen={supplierId} backdrop="static" keyboard={false} size="xl">
                <AvForm ref={editGeneralInfosFormRef} onValidSubmit={(e, values) => {
                    setStoredParentThirdPartyId(
                        supplierId,
                        selectedParentThirdParty?.value
                    );
                    onParentThirdPartyChange && onParentThirdPartyChange(
                        selectedParentThirdParty || null
                    );

                    updateSupplierGeneralInfosMutation({
                        supplier: supplierId,
                        generalInfo: {
                            ...values,
                            subType: values.subType
                        }
                    });
                }} className="needs-validation m-2">
                    <ModalHeader toggle={closeModal}>
                        {t("General Information")}
                    </ModalHeader>

                    <ModalBody>
                        {renderModalBody()}
                    </ModalBody>

                    <ModalFooter>
                        <Button onClick={closeModal} color="primary" type="button" outline>
                            {t('Cancel')}
                        </Button>

                        <Button disabled={updateSupplierGeneralInfosMutationInProcess} color="primary" type="submit" size="md">
                            {
                                updateSupplierGeneralInfosMutationInProcess ? (
                                    <Spinner className="me-2" animation="border" variant="white" size="sm" />
                                ) : (
                                    <i className="ri-pencil-line align-middle me-2"></i>
                                )

                            }
                            {t('Update')}
                        </Button>
                    </ModalFooter>
                </AvForm>
            </Modal>

            <Modal isOpen={showIndustryDeletionError} backdrop="static" keyboard={false} size="lg">
                <ModalHeader toggle={() => setShowIndustryDeletionError(null)}>
                    <div className="d-flex align-items-center justify-content-start">
                        {t('Deletion Error')}
                    </div>
                </ModalHeader>

                <ModalBody>
                    <Alert color="danger" className="d-flex justify-content-start">
                        <div className="d-flex align-items-center me-2">
                            <i className="ri-close-circle-line" style={{ fontSize: '32px' }} />
                        </div>
                        <div className="d-flex align-items-start justify-content-center flex-column">
                            <strong>{t('Error')}</strong>
                            {t("In order to remove the desired industry, the following products must be removed from the supplier's product list")}
                        </div>
                    </Alert>
                    <ul>
                        {(showIndustryDeletionError?.products || []).map((p) => <li className="mb-2">{`${p.class} - ${t(p.name)}`}</li>)}
                    </ul>
                </ModalBody>

                <ModalFooter>
                    <Button onClick={() => setShowIndustryDeletionError(null)} color="primary" size="md">
                        {t('Close')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
};

export default withNamespaces()(EditSupplierGeneralInfosModal);